import { ApiError } from "@/lib/api-client";
import { formatWaitMinutes, readRetryAfterSeconds } from "@/lib/retry-after";

// Maps a failed self-service `POST /profile/change-email` to a UI outcome
// (Story 14.9 AC6 / 14.10). Per-slice mapper, the `invitation-error.ts`
// convention: branch ONLY on the structured `error.code` / `error.status` and
// the 422 `loc` — NEVER on the server's raw `msg`. No global registry, no
// toasts; the dialog renders these inline.
//
// The two deterministic failures land on the FIELD the user can actually fix —
// a banner saying "wrong password" above a form with two inputs makes the user
// guess which one. A 429 is the only state with no field to blame, and it is a
// settled one (`info`, not `danger`): the budget refills on its own, so alarming
// copy would misreport a wait as a fault.
//
// [Source: src/application/authentication/change_email/* — 401 AUTH-001
//  invalid credentials (password confirmation failed); 409 AUTH-014
//  email_in_use; 429 AUTH-015 change_email_rate_limited; 422 schema validation
//  + 422 AUTH-017 unsupported address shape; 403 AUTH-018 account deactivated;
//  403 AUTH-009 force_password_change (raised by get_current_user, BEFORE the
//  slice — a live account, not a blocked one).]

export interface ChangeEmailBanner {
  /** `info` = a settled state that resolves itself; `warn`/`danger` = check/transport. */
  variant: "info" | "warn" | "danger";
  title: string;
  body: string;
}

/** Inline messages keyed to the dialog's field names. */
export interface ChangeEmailFieldErrors {
  newEmail?: string;
  passwordConfirmation?: string;
}

export interface MappedChangeEmailError {
  /** Banner descriptor, or null when the failure is fully handled inline. */
  banner: ChangeEmailBanner | null;
  /** Per-field messages, so the user is never told to "check the details" blindly. */
  fieldErrors: ChangeEmailFieldErrors;
}

const GENERIC: ChangeEmailBanner = {
  variant: "danger",
  title: "Couldn't change your email",
  body: "Something went wrong changing your email address. Please try again.",
};

// 429 AUTH-015 — the endpoint carries a per-user sliding window (it verifies a
// password, so it is a brute-force oracle). A settled state: the window rolls
// forward on its own, so no dead retry button and no alarm.
//
// The server also reports how long the wait is, under `details.retry_after_s`
// (the `Retry-After` header carries it too, but `ApiError` exposes only
// code/status/details, so the header never reaches this mapper). Naming the wait
// is what turns "come back later" into an instruction — without it the user's
// only strategy is to keep retrying, which is exactly what the limiter is there
// to stop. Rounded UP to the minute above 90s: a countdown to the second implies
// a precision the sliding window does not have.
const RATE_LIMITED: ChangeEmailBanner = {
  variant: "info",
  title: "Too many attempts",
  body: "You've tried to change your email several times in a short window. Please wait a little while before trying again.",
};

// The wait is phrased in seconds up to 90s and in whole minutes above it: "in
// about 45 seconds" is actionable, "in about 1 minute" for the same wait is not.
// The minute half is the shared `formatWaitMinutes` — the change-PASSWORD
// lockout (F2) rounds the same way, and one rounding rule beats two.
function formatWait(seconds: number): string {
  if (seconds <= 90) return `${Math.ceil(seconds)} seconds`;
  return formatWaitMinutes(seconds);
}

function rateLimitedBanner(details: unknown): ChangeEmailBanner {
  const seconds = readRetryAfterSeconds(details);
  if (seconds === null) return RATE_LIMITED;
  return {
    ...RATE_LIMITED,
    body: `You've tried to change your email several times in a short window. Please try again in about ${formatWait(seconds)}.`,
  };
}

// 403 AUTH-018 — the account is deactivated (the command's account_disabled
// outcome). A settled account state; the user cannot fix it from this dialog.
const ACCOUNT_DISABLED: ChangeEmailBanner = {
  variant: "warn",
  title: "Your account isn't active",
  body: "This account can't change its sign-in email right now. Contact CITS support for help.",
};

// 403 AUTH-009 — force_password_change is set, so `get_current_user` refuses
// every route except /auth/change-password. The account is perfectly live; the
// user is one screen away from clearing it. This branch exists because the two
// refusals share a status: mapping on `error.status === 403` alone told this user
// their account was inactive and pointed them at support, which is both wrong and
// a dead end. Branch on the CODE, and name the screen that fixes it.
const PASSWORD_CHANGE_REQUIRED: ChangeEmailBanner = {
  variant: "warn",
  title: "Set a new password first",
  body: "Your account still has a temporary password. Set a new one on the Forced password change screen, then come back to update your email.",
};

/**
 * Translate one 422 detail item into our own field message. The field is read
 * from the END of `loc` (e.g. `["body","new_email"]` → `"new_email"`) — never
 * from the server's message text.
 */
function applyDetailItem(item: unknown, into: ChangeEmailFieldErrors): void {
  if (!item || typeof item !== "object") return;
  const loc = (item as { loc?: unknown }).loc;
  const field = Array.isArray(loc) && loc.length > 0 ? String(loc[loc.length - 1]) : "";

  if (field === "new_email" && !into.newEmail) {
    into.newEmail = "Enter a valid email address.";
  } else if (field === "password_confirmation" && !into.passwordConfirmation) {
    into.passwordConfirmation = "Enter your current password.";
  }
}

function parse422Details(details: unknown): ChangeEmailFieldErrors {
  const fieldErrors: ChangeEmailFieldErrors = {};
  if (Array.isArray(details)) {
    for (const item of details) applyDetailItem(item, fieldErrors);
  }
  return fieldErrors;
}

/** Map a change-email failure to inline field errors + an optional banner. */
export function toChangeEmailError(error: unknown): MappedChangeEmailError {
  if (error instanceof ApiError) {
    // AUTH-001 — the password confirmation failed. Inline on the password
    // field: this is the ONE input the user can correct, and a banner would
    // leave them guessing which of the two is wrong.
    if (error.code === "AUTH-001") {
      return {
        fieldErrors: { passwordConfirmation: "That password isn't correct." },
        banner: null,
      };
    }
    // AUTH-014 — the address is already in use (by anyone, the caller included).
    if (error.code === "AUTH-014") {
      return {
        fieldErrors: {
          newEmail: "That email address is already in use. Try a different one.",
        },
        banner: null,
      };
    }
    if (error.status === 422) {
      const fieldErrors = parse422Details(error.details);
      const handledInline = Object.keys(fieldErrors).length > 0;
      return {
        fieldErrors,
        banner: handledInline
          ? null
          : {
              variant: "warn",
              title: "Check the details",
              body: "Please review the fields and try again.",
            },
      };
    }
    // Any other 409 (unknown code) is still a uniqueness conflict in practice —
    // attach it to the email field rather than dropping the user into a generic
    // failure with no actionable field.
    if (error.status === 409) {
      return {
        fieldErrors: {
          newEmail: "That email address is already in use. Try a different one.",
        },
        banner: null,
      };
    }
    if (error.status === 429) {
      return { fieldErrors: {}, banner: rateLimitedBanner(error.details) };
    }
    // The two 403s this endpoint can return mean opposite things — code first,
    // status only as the fallback for an unrecognised one.
    if (error.code === "AUTH-009") {
      return { fieldErrors: {}, banner: PASSWORD_CHANGE_REQUIRED };
    }
    if (error.status === 403) {
      return { fieldErrors: {}, banner: ACCOUNT_DISABLED };
    }
    // Any other coded error falls through to the generic danger banner.
  }
  // NETWORK_ERROR / AuthExpiredError / unknown → generic, no internals leaked.
  return { fieldErrors: {}, banner: GENERIC };
}
